/**
 * Informes fiscales — `OD-0041` (ACCEPTED), plan Business (`tax_insights`).
 *
 * Por trimestre natural: el IVA repercutido por tipo, las retenciones de IRPF
 * que han hecho los clientes, el IVA soportado de las compras anotadas
 * (`OD-0056`) y los próximos plazos de la AEAT. Debajo, las facturas y las
 * compras que forman cada cifra (`D-12`, `OD-0056 D-06`).
 *
 * El IVA soportado va con su aviso SIEMPRE a la vista (no es lo que te puedes
 * deducir) y con una guía plegada escrita para quien no sabe de impuestos
 * (`OD-0056 D-05`). Las dos cifras de IVA no se restan (`D-03`).
 *
 * ## Lo que esta pantalla NO decide
 *
 * - **Ninguna cifra** (`URS §129`): todo llega sumado de la API. Aquí no se
 *   suma, ni se resta, ni se compara un importe.
 * - **Ni qué trimestre existe** (`D-03`): la lista la da la API con el día de
 *   Madrid.
 * - **Ni la proximidad de un plazo** (`D-11`): llega con su tono.
 * - **Ni si el plan la incluye** (`D-13`): fuera de Business la API contesta
 *   402 y aquí se explica UNA vez, sin ceros ni guiones.
 *
 * Y lo que dice siempre, encima de las cifras: que es una estimación parcial,
 * que no es una declaración y que Faktelio no presenta nada (`D-02`).
 */
import { useCallback, useEffect, useState } from "react";

import { api, type InformeFiscal, type IvaSoportadoDelInforme, type ObligacionFiscal } from "../api/cliente";
import { t } from "../i18n/es";
import { importeLegible } from "../ui/formato";
import { Icono } from "../ui/Iconos";
import { Aviso, Boton, FilaPulsable, Selector, Tarjeta } from "../ui/primitivas";
import { BotonExportacion, PanelExportacion, useEstadoExportacion } from "./PanelExportacion";
import { diaLegible } from "./PantallaOportunidades";

type Estado =
  | { readonly tipo: "cargando" }
  | { readonly tipo: "error" }
  | { readonly tipo: "sin-permiso" }
  | { readonly tipo: "solo-business" }
  | { readonly tipo: "datos"; readonly datos: InformeFiscal };

/**
 * Un plazo del calendario. `hoy` es el día de Madrid del informe: con él se
 * sabe si la domiciliación ya cerró. Lo usa también la tarjeta de Inicio.
 */
export function Obligacion({ o, hoy }: { o: ObligacionFiscal; hoy: string }) {
  const nombre = t.informes.modelos[o.modelo] ?? o.modelo;
  if (!o.publicado) {
    return (
      <li className="informe-plazo informe-plazo-sin-publicar">
        <span className="informe-plazo-modelo">
          {nombre} · {t.informes.periodo(o.periodo)}
        </span>
        <span className="ayuda-neutra">{t.informes.noPublicado(o.calendario, o.plazoGeneral)}</span>
      </li>
    );
  }
  return (
    <li className={`informe-plazo informe-plazo-${o.proximidad.toLowerCase()}${o.estado === "TERMINADO" ? " informe-plazo-terminado" : ""}`}>
      <span className="informe-plazo-modelo">
        {nombre} · {t.informes.periodo(o.periodo)}
      </span>
      <span>{t.informes.plazo(diaLegible(o.desde), diaLegible(o.hasta))}</span>
      <span className="ayuda-neutra">
        <span className={`insignia informe-insignia-${o.estado.toLowerCase()}`}>{t.informes.estados[o.estado]}</span>
        {o.estado !== "TERMINADO" ? ` · ${t.informes.quedan(o.diasRestantes)}` : ""}
      </span>
      {o.domiciliacionHasta !== null && o.estado !== "TERMINADO" ? (
        <span className="ayuda-neutra">
          {o.domiciliacionHasta < hoy
            ? t.informes.domiciliacionCerrada(diaLegible(o.domiciliacionHasta))
            : t.informes.domiciliacion(diaLegible(o.domiciliacionHasta))}
        </span>
      ) : null}
    </li>
  );
}

/** `OD-0056 D-04`: la cifra, o por qué no la hay. Nunca un «0,00» en su lugar. */
function TarjetaSoportado({ s, onIrACompras }: { s: IvaSoportadoDelInforme; onIrACompras: () => void }) {
  if (!s.disponible) {
    return (
      <section className="informe-cifra informe-cifra-sin-datos" aria-labelledby="informe-soportado">
        <h3 id="informe-soportado" className="informe-cifra-titulo">
          {t.informes.soportadoTitulo}
        </h3>
        <p className="informe-cifra-sin-datos-texto">
          {t.informes.soportadoSinCifra[s.motivo] ?? ""} {s.compras.join(", ")}
        </p>
        {s.motivo === "SIN_COMPRAS_ANOTADAS" || s.motivo === "NINGUNA_EN_EL_TRIMESTRE" ? (
          <Boton variante="secundario" onClick={onIrACompras}>
            {t.informes.irACompras}
          </Boton>
        ) : null}
      </section>
    );
  }
  return (
    <section className="informe-cifra" aria-labelledby="informe-soportado">
      <h3 id="informe-soportado" className="informe-cifra-titulo">
        {t.informes.soportadoTitulo}
      </h3>
      <p className="informe-cifra-importe">{t.informes.soportadoCifra(importeLegible(s.soportado))}</p>
      <p className="ayuda-neutra">{t.informes.soportadoBase(importeLegible(s.base), s.compras.length)}</p>
      <p className="ayuda-neutra">{t.informes.soportadoAyuda}</p>
    </section>
  );
}

/**
 * `OD-0056 D-05`: el aviso, a la vista junto a la cifra, y la guía plegada:
 * quien ya sabe lo que es no tiene por qué leerla cada vez.
 */
function ExplicacionSoportado({ s }: { s: IvaSoportadoDelInforme }) {
  if (!s.disponible && (s.motivo === "ROL_SIN_COMPRAS" || s.motivo === "PLAN_SIN_COMPRAS")) return null;
  return (
    <>
      {s.disponible ? (
        <Aviso tono="info" titulo={t.informes.soportadoAvisoTitulo}>
          {t.informes.soportadoAviso}
        </Aviso>
      ) : null}
      <details className="desglose guia-soportado">
        <summary>{t.informes.soportadoGuia.titulo}</summary>
        {t.informes.soportadoGuia.secciones.map((g) => (
          <section key={g.titulo}>
            <h4>{g.titulo}</h4>
            {g.parrafos.map((p) => (
              <p key={p}>{p}</p>
            ))}
            {g.puntos.length > 0 ? (
              <ul>
                {g.puntos.map((x) => (
                  <li key={x}>{x}</li>
                ))}
              </ul>
            ) : null}
          </section>
        ))}
      </details>
    </>
  );
}

/** Una tabla por tipo de IVA: la del repercutido y la del soportado son la misma forma. */
function TablaPorTipo({
  id,
  titulo,
  filas,
}: {
  id: string;
  titulo: string;
  filas: ReadonlyArray<{ readonly tratamiento: string; readonly tipo: string | null; readonly causa?: string | null; readonly base: string; readonly cuota: string | null }>;
}) {
  if (filas.length === 0) return null;
  return (
    <section aria-labelledby={id}>
      <h3 id={id} className="subtitulo">
        {titulo}
      </h3>
      <table className="informe-tabla">
        <thead>
          <tr>
            <th scope="col">{t.informes.columnaTipo}</th>
            <th scope="col">{t.informes.columnaBase}</th>
            <th scope="col">{t.informes.columnaCuota}</th>
          </tr>
        </thead>
        <tbody>
          {filas.map((l) => (
            <tr key={`${l.tratamiento}|${l.tipo ?? l.causa ?? ""}`}>
              <th scope="row">
                {l.tratamiento === "SUJETA_NO_EXENTA" && l.tipo !== null
                  ? t.informes.tipoSujeta(l.tipo)
                  : `${t.informes.tratamientos[l.tratamiento] ?? l.tratamiento}${l.causa !== undefined && l.causa !== null ? ` (${l.causa})` : ""}`}
              </th>
              <td>{importeLegible(l.base)} €</td>
              <td>{l.cuota === null ? t.informes.sinCuota : `${importeLegible(l.cuota)} €`}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

export function PantallaInformesFiscales({
  onAbrirFactura,
  onAbrirCompra,
  onIrACompras,
  onVerPlan,
  onSesionCaducada,
  hayConexion,
}: {
  onAbrirFactura: (facturaId: string) => void;
  onAbrirCompra: (compraId: string) => void;
  onIrACompras: () => void;
  onVerPlan: () => void;
  onSesionCaducada: () => void;
  /** Para la exportación: sin red no se descarga. */
  hayConexion: boolean;
}) {
  const [trimestre, setTrimestre] = useState<string | null>(null);
  const [estado, setEstado] = useState<Estado>({ tipo: "cargando" });
  // OD-0054-ENMIENDA-01: aquí se exportan las dos, ventas y proveedores.
  const exportacion = useEstadoExportacion();
  const [exportacionAbierta, setExportacionAbierta] = useState(false);

  const cargar = useCallback(async (): Promise<void> => {
    setEstado({ tipo: "cargando" });
    const r = await api.informeFiscal(trimestre);
    if (!r.ok) {
      if (r.estado === 401) onSesionCaducada();
      else if (r.estado === 402) setEstado({ tipo: "solo-business" });
      else if (r.estado === 403) setEstado({ tipo: "sin-permiso" });
      else setEstado({ tipo: "error" });
      return;
    }
    setEstado({ tipo: "datos", datos: r.datos });
  }, [trimestre, onSesionCaducada]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  if (estado.tipo === "solo-business") {
    return (
      <Tarjeta titulo={t.informes.titulo}>
        <div className="estado-vacio">
          <span className="estado-vacio-icono" aria-hidden="true">
            <Icono nombre="informes" />
          </span>
          <p className="estado-vacio-titulo">{t.informes.soloBusinessTitulo}</p>
          <p>{t.informes.soloBusiness}</p>
          <Boton onClick={onVerPlan}>{t.informes.verPlanes}</Boton>
        </div>
      </Tarjeta>
    );
  }

  if (estado.tipo !== "datos") {
    return (
      <Tarjeta titulo={t.informes.titulo}>
        {estado.tipo === "cargando" ? <p>{t.informes.cargando}</p> : null}
        {estado.tipo === "sin-permiso" ? <Aviso tono="aviso">{t.informes.sinPermiso}</Aviso> : null}
        {estado.tipo === "error" ? (
          <>
            <Aviso tono="error">{t.informes.error}</Aviso>
            <Boton variante="secundario" onClick={() => void cargar()}>
              {t.informes.reintentar}
            </Boton>
          </>
        ) : null}
      </Tarjeta>
    );
  }

  const d = estado.datos;
  const inf = d.informe;
  const s = d.ivaSoportado;
  // D-10: la fuente oficial de las fechas que se enseñan, si se enseña alguna.
  const conFecha = [...d.declaracion, ...d.calendario.proximas].find((o) => o.publicado);
  // Lo que ya sale en «Dónde se declara» no se repite debajo.
  const yaDichas = new Set(d.declaracion.map((o) => `${o.modelo}|${o.periodo}`));
  const proximas = d.calendario.proximas.filter((o) => !yaDichas.has(`${o.modelo}|${o.periodo}`));
  const fuente = conFecha !== undefined && conFecha.publicado ? conFecha.fuente : null;

  return (
    <Tarjeta titulo={t.informes.titulo}>
      <div className="informe-cabecera">
        <Selector
          id="informe-trimestre"
          etiqueta={t.informes.trimestre}
          valor={d.trimestre.etiqueta}
          opciones={d.trimestres.map((x) => ({ valor: x, texto: t.informes.nombreTrimestre(x) }))}
          onChange={(v) => setTrimestre(v)}
        />
        <p className="ayuda-neutra">
          {t.informes.aFecha(diaLegible(d.aFecha))} {d.trimestre.enCurso ? t.informes.enCurso : ""}
        </p>
        {exportacion !== null ? <BotonExportacion abierto={exportacionAbierta} onClick={() => setExportacionAbierta((v) => !v)} /> : null}
      </div>
      {exportacion !== null && exportacionAbierta ? (
        <PanelExportacion
          // Con el trimestre que se está mirando; cambiarlo abre el panel de nuevo.
          key={d.trimestre.etiqueta}
          estado={exportacion}
          hayConexion={hayConexion}
          alcance="todo"
          trimestreInicial={d.trimestre.etiqueta}
          onCerrar={() => setExportacionAbierta(false)}
        />
      ) : null}

      <Aviso tono="info" titulo={t.informes.parcialTitulo}>
        {t.informes.parcial}
      </Aviso>

      {!inf.calculado ? (
        <>
          <Aviso tono="aviso" titulo={t.informes.sinDatosTitulo}>
            {t.informes.sinDatos[inf.motivo] ?? ""} {inf.facturas.join(", ")}
          </Aviso>
          <div className="informe-cifras">
            <TarjetaSoportado s={s} onIrACompras={onIrACompras} />
          </div>
          <ExplicacionSoportado s={s} />
        </>
      ) : (
        <>
          <div className="informe-cifras">
            <section className="informe-cifra" aria-labelledby="informe-iva">
              <h3 id="informe-iva" className="informe-cifra-titulo">
                {t.informes.ivaTitulo}
              </h3>
              <p className="informe-cifra-importe">{t.informes.ivaCifra(importeLegible(inf.iva.repercutido))}</p>
              <p className="ayuda-neutra">{t.informes.ivaBase(importeLegible(inf.iva.base), inf.iva.facturas)}</p>
              <p className="ayuda-neutra">{t.informes.ivaAyuda}</p>
            </section>
            <TarjetaSoportado s={s} onIrACompras={onIrACompras} />
            <section className="informe-cifra" aria-labelledby="informe-retenciones">
              <h3 id="informe-retenciones" className="informe-cifra-titulo">
                {t.informes.retencionesTitulo}
              </h3>
              <p className="informe-cifra-importe">{t.informes.retencionesCifra(importeLegible(inf.retenciones.importe))}</p>
              <p className="ayuda-neutra">{t.informes.retencionesBase(inf.retenciones.facturas)}</p>
              <p className="ayuda-neutra">{t.informes.retencionesAyuda}</p>
            </section>
          </div>

          <ExplicacionSoportado s={s} />

          <TablaPorTipo id="informe-por-tipo" titulo={t.informes.porTipo} filas={inf.iva.porTipo} />

          {inf.retenciones.porTipo.length > 0 ? (
            <ul className="informe-retenciones">
              {inf.retenciones.porTipo.map((r) => (
                <li key={r.tipo}>{t.informes.retencionTipo(r.tipo, importeLegible(r.base), importeLegible(r.importe), r.facturas)}</li>
              ))}
            </ul>
          ) : (
            <p className="ayuda-neutra">{t.informes.sinRetenciones}</p>
          )}
        </>
      )}

      {d.declaracion.length > 0 ? (
        <section aria-labelledby="informe-declaracion">
          <h3 id="informe-declaracion" className="subtitulo">
            {t.informes.declaracionTitulo}
          </h3>
          <ul className="informe-plazos">
            {d.declaracion.map((o) => (
              <Obligacion key={`${o.modelo}|${o.periodo}`} o={o} hoy={d.aFecha} />
            ))}
          </ul>
        </section>
      ) : null}

      <section aria-labelledby="informe-calendario">
        <h3 id="informe-calendario" className="subtitulo">
          {t.informes.calendarioTitulo}
        </h3>
        <p className="ayuda-neutra">{t.informes.calendarioAyuda}</p>
        {proximas.length === 0 ? (
          <p className="ayuda-neutra">{t.informes.sinProximos}</p>
        ) : (
          <ul className="informe-plazos">
            {proximas.map((o) => (
              <Obligacion key={`${o.modelo}|${o.periodo}`} o={o} hoy={d.aFecha} />
            ))}
          </ul>
        )}
        {d.calendario.obligado === "ENTIDAD" ? <p className="ayuda-neutra">{t.informes.soloPersonas}</p> : null}
        {fuente !== null ? <p className="ayuda-neutra">{t.informes.fuente(fuente.titulo, diaLegible(d.calendario.comprobadoEl))}</p> : null}
        <p className="ayuda-neutra">{t.informes.confirma}</p>
      </section>

      {inf.calculado ? (
        <section aria-labelledby="informe-facturas">
          <h3 id="informe-facturas" className="subtitulo">
            {t.informes.facturasTitulo}
          </h3>
          {inf.facturas.length === 0 ? (
            <Aviso tono="info">{t.informes.sinFacturas}</Aviso>
          ) : (
            <>
              <p className="ayuda-neutra">{t.informes.facturasAyuda}</p>
              <ul className="lista-filas">
                {inf.facturas.map((f) => (
                  <FilaPulsable key={f.invoiceId} onClick={() => onAbrirFactura(f.invoiceId)}>
                    <span className="fila-titulo">
                      {f.numero} · {t.informes.expedida(diaLegible(f.fechaExpedicion))}
                    </span>
                    <span className="ayuda-neutra">
                      {t.informes.filaFactura(importeLegible(f.base), importeLegible(f.cuota))}
                      {f.retencion !== "0,00" ? t.informes.filaRetencion(importeLegible(f.retencion)) : ""}
                    </span>
                    {f.clase !== "ORDINARIA" ? <span className="ayuda-neutra">{t.informes.clases[f.clase]}</span> : null}
                    {f.avisos.map((a) => (
                      <span key={a} className="ayuda-neutra informe-aviso-factura">
                        {t.informes.avisos[a] ?? a}
                      </span>
                    ))}
                  </FilaPulsable>
                ))}
              </ul>
            </>
          )}
        </section>
      ) : null}

      {inf.excluidas.length > 0 ? (
        <section aria-labelledby="informe-excluidas">
          <h3 id="informe-excluidas" className="subtitulo">
            {t.informes.excluidasTitulo}
          </h3>
          <ul className="informe-excluidas">
            {inf.excluidas.map((e) => (
              <li key={e.invoiceId}>
                <button type="button" className="enlace-en-texto" onClick={() => onAbrirFactura(e.invoiceId)}>
                  {t.informes.excluida(e.numero, diaLegible(e.fechaExpedicion))}
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {s.disponible ? (
        <section aria-labelledby="informe-compras">
          <h3 id="informe-compras" className="subtitulo">
            {t.informes.comprasTitulo}
          </h3>
          <p className="ayuda-neutra">{t.informes.comprasAyuda}</p>
          <TablaPorTipo id="informe-compras-por-tipo" titulo={t.informes.comprasPorTipo} filas={s.porTipo} />
          <ul className="lista-filas">
            {s.compras.map((c) => (
              <FilaPulsable key={c.purchaseId} onClick={() => onAbrirCompra(c.purchaseId)}>
                <span className="fila-titulo">{t.informes.filaCompraTitulo(c.registro, c.proveedor, c.numeroDelProveedor)}</span>
                <span className="ayuda-neutra">{t.informes.filaCompra(diaLegible(c.fechaAnotacion), importeLegible(c.base), importeLegible(c.cuota))}</span>
                {c.avisos.map((a) => (
                  <span key={a} className="ayuda-neutra informe-aviso-factura">
                    {t.informes.avisosCompra[a] ?? a}
                  </span>
                ))}
              </FilaPulsable>
            ))}
          </ul>
        </section>
      ) : null}

      {s.excluidas.length > 0 ? (
        <section aria-labelledby="informe-compras-excluidas">
          <h3 id="informe-compras-excluidas" className="subtitulo">
            {t.informes.comprasExcluidasTitulo}
          </h3>
          <ul className="informe-excluidas">
            {s.excluidas.map((e) => (
              <li key={e.purchaseId}>
                <button type="button" className="enlace-en-texto" onClick={() => onAbrirCompra(e.purchaseId)}>
                  {t.informes.compraExcluida(e.registro, e.proveedor)}
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

    </Tarjeta>
  );
}
